/** @format */

export function taskTripName(task) {
  const short = String(
    task?.shortName ||
      task?.short_name ||
      task?.templateShortName ||
      task?.template_short_name ||
      '',
  ).trim();
  if (short) return short;
  return String(task?.label || '').trim();
}

export function packTripLabel(tasks, fallback = '') {
  const labels = [...(tasks || [])]
    .sort((left, right) => {
      const sequence =
        (Number(left?.sequence) || 0) - (Number(right?.sequence) || 0);
      if (sequence) return sequence;
      return (Number(left?.id) || 0) - (Number(right?.id) || 0);
    })
    .map(taskTripName)
    .filter(Boolean);
  return labels.length ? labels.join(' + ') : fallback;
}

export function workItemFromApi(data) {
  const kind = data?.kind || 'pack';
  if (kind === 'trip') {
    const estimatedHours =
      Number(data.estimated_hours ?? data.estimatedHours) || 0;
    const trip = {
      id: data.id,
      label: data.label || '',
      estimatedHours,
      date: data.date || '',
      groups: data.groups || [],
      interventionId: data.intervention_id ?? data.interventionId ?? null,
      chantierId: data.project_id ?? data.chantierId ?? null,
      projectId: data.project_id ?? data.projectId ?? null,
      extraLow: Boolean(data.extra_low ?? data.extraLow),
      low: Boolean(data.low),
      medium: data.medium !== false,
      high: Boolean(data.high),
      extraHigh: Boolean(data.extra_high ?? data.extraHigh),
      priorityColor: (data.color || data.priorityColor || '').trim(),
      isFinalisation: Boolean(data.is_finalisation ?? data.isFinalisation),
      isImportedFromBigchange: Boolean(
        data.is_imported_from_bigchange ?? data.isImportedFromBigchange,
      ),
      version: Number(data.version) > 0 ? Number(data.version) : 0,
      updatedDate: data.updated_date || data.updatedDate || null,
    };
    return {
      kind: 'trip',
      id: data.id != null ? `trip-${data.id}` : data.id,
      tripId: data.id,
      interventionId: trip.interventionId,
      taskIds: [],
      label: trip.label,
      durationMinutes: Math.round(estimatedHours * 60),
      estimatedHours,
      minimumTechnicianCount: 1,
      forcedTechnicianIds: [],
      tasks: [],
      groups: trip.groups,
      trip,
    };
  }
  const estimatedHours =
    Number(data.estimated_hours) ||
    (Number(data.duration_minutes) || 0) / 60 ||
    0;
  const crew = Math.max(Number(data.minimum_technician_count) || 1, 1);
  const forced = (data.forced_technician_ids || [])
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id));
  const techIds = [
    ...forced,
    ...Array.from({ length: Math.max(0, crew - forced.length) }, () => null),
  ].slice(0, crew);
  return {
    kind,
    id: data.id,
    tripId: null,
    interventionId: data.intervention_id ?? null,
    taskIds: (data.task_ids || []).map(Number),
    label: data.label || '',
    durationMinutes:
      Number(data.duration_minutes) || Math.round(estimatedHours * 60),
    estimatedHours,
    minimumTechnicianCount: crew,
    forcedTechnicianIds: forced,
    tasks: (data.tasks || []).map((task) => ({
      id: task.id,
      label: task.label || '',
      shortName: task.shortName || task.short_name || '',
      durationMinutes: Number(task.duration_minutes) || 0,
      minimumTechnicianCount: Number(task.minimum_technician_count) || 1,
    })),
    groups: [
      {
        technician_count: crew,
        duration: estimatedHours,
        technician_ids: techIds,
      },
    ],
    trip: null,
  };
}

export function remainingWorkItemFromPacks(packs) {
  const list = (packs || []).filter((item) => item.kind === 'pack');
  if (!list.length) return null;
  if (list.length === 1) {
    return { ...list[0], kind: 'remaining' };
  }
  const taskIds = list.flatMap((item) => item.taskIds || []);
  const durationMinutes = list.reduce(
    (sum, item) => sum + (Number(item.durationMinutes) || 0),
    0,
  );
  const crew = Math.max(
    ...list.map((item) => Number(item.minimumTechnicianCount) || 1),
    1,
  );
  const forced = [];
  const seen = new Set();
  list.forEach((item) => {
    (item.forcedTechnicianIds || []).forEach((id) => {
      const num = Number(id);
      if (!Number.isFinite(num) || seen.has(num)) return;
      seen.add(num);
      forced.push(num);
    });
  });
  const labels = list.map((item) => item.label).filter(Boolean);
  const techIds = [
    ...forced,
    ...Array.from({ length: Math.max(0, crew - forced.length) }, () => null),
  ].slice(0, crew);
  return {
    kind: 'remaining',
    id: `remaining-${list[0].interventionId || list[0].id}`,
    tripId: null,
    interventionId: list[0].interventionId ?? null,
    taskIds,
    label: labels.join(' + '),
    durationMinutes,
    estimatedHours: durationMinutes / 60,
    minimumTechnicianCount: crew,
    forcedTechnicianIds: forced,
    tasks: list.flatMap((item) => item.tasks || []),
    groups: [
      {
        technician_count: crew,
        duration: durationMinutes / 60,
        technician_ids: techIds,
      },
    ],
    trip: null,
  };
}

export function taskWorkItemFromPack(pack, task) {
  const durationMinutes = Math.max(Number(task.durationMinutes) || 0, 15);
  const crew = Math.max(Number(task.minimumTechnicianCount) || 1, 1);
  return {
    kind: 'task',
    id: `task-${task.id}`,
    tripId: null,
    interventionId: pack.interventionId ?? null,
    taskIds: [task.id],
    label: taskTripName(task) || pack.label,
    durationMinutes,
    estimatedHours: durationMinutes / 60,
    minimumTechnicianCount: crew,
    forcedTechnicianIds: pack.forcedTechnicianIds || [],
    tasks: [task],
    groups: [
      {
        technician_count: crew,
        duration: durationMinutes / 60,
        technician_ids: Array.from({ length: crew }, () => null),
      },
    ],
    trip: null,
  };
}

export function formatWorkItemHours(hours) {
  const n = Number(hours);
  if (!Number.isFinite(n)) return '0';
  return n % 1 === 0 ? String(n) : n.toFixed(1).replace('.', ',');
}

export function formatWorkItemMeta(item) {
  const crew = Math.max(Number(item?.minimumTechnicianCount) || 1, 1);
  return `${crew} tech × ${formatWorkItemHours(item?.estimatedHours)} h`;
}

export function formatTaskDurationLabel(minutes) {
  return `${formatWorkItemHours((Number(minutes) || 0) / 60)} h`;
}

export function remainingTasksFromWorkItems(workItems, excludeTaskIds = []) {
  const exclude = new Set(
    (excludeTaskIds || [])
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id)),
  );
  const seen = new Set();
  const tasks = [];
  (workItems || []).forEach((item) => {
    if (!item || item.kind === 'trip') return;
    const nested = item.tasks?.length
      ? item.tasks
      : (item.taskIds || []).map((id) => ({
          id,
          label: item.label || '',
          durationMinutes: item.durationMinutes,
          minimumTechnicianCount: item.minimumTechnicianCount,
        }));
    nested.forEach((task) => {
      const id = Number(task.id);
      if (!Number.isFinite(id) || exclude.has(id) || seen.has(id)) return;
      seen.add(id);
      tasks.push({
        id,
        label: task.label || '',
        shortName: task.shortName || '',
        durationMinutes: Number(task.durationMinutes) || 0,
        minimumTechnicianCount: Number(task.minimumTechnicianCount) || 1,
      });
    });
  });
  return tasks;
}
